import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactFlow, Background, Controls } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useChannels, useTranslation } from '@hooks';
import { alertActions, brandsActions } from '@actions';
import { automationsService } from '@services';
import { handleApiError } from '@helpers/api';
import { AUTOMATION_NODE_TYPE, getNodeKind } from '@constants/automations';
import { computeGraphIssues } from './validation';
import FlowNode from './FlowNode';
import InsertEdge from './InsertEdge';
import EditorHeader from './EditorHeader';
import RightPanel from './RightPanel';
import ActionPalette from './ActionPalette';
import NodeSettings from './NodeSettings';
import { layoutFlow } from './layout';
import {
    RF_NODE_TYPE,
    RF_EDGE_TYPE,
    emptyAutomation,
    modelToFlow,
    flowToModel,
    insertNodeOnEdge,
    deleteNode,
} from './flow';

const Editor = ({ automationId, labels = [], readOnly = false, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { channels } = useChannels();

    const [base, setBase] = useState(null);
    const [graph, setGraph] = useState({ nodes: [], edges: [] });
    const [panel, setPanel] = useState({ mode: null, edgeId: null, nodeId: null });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        dispatch(brandsActions.getAll());
    }, [dispatch]);

    useEffect(() => {
        let active = true;
        const load = async () => {
            if (automationId) {
                try {
                    const model = await automationsService.getById(automationId);
                    if (!active) return;
                    const flow = modelToFlow(model);
                    setBase(model);
                    setGraph({ nodes: flow.nodes, edges: flow.edges });
                } catch (error) {
                    handleApiError(error, 'alertMessages.fetchFail');
                }
            } else {
                const fresh = emptyAutomation();
                setBase(fresh.model);
                setGraph({ nodes: fresh.nodes, edges: fresh.edges });
            }
        };
        load();
        return () => {
            active = false;
        };
    }, [automationId]);

    const nodeTypes = useMemo(() => ({ [RF_NODE_TYPE]: FlowNode }), []);
    const edgeTypes = useMemo(() => ({ [RF_EDGE_TYPE]: InsertEdge }), []);

    const onInsert = useCallback(edgeId => setPanel({ mode: 'palette', edgeId, nodeId: null }), []);
    const onDeleteNode = useCallback(nodeId => {
        setGraph(g => deleteNode(g.nodes, g.edges, nodeId));
        setPanel({ mode: null, edgeId: null, nodeId: null });
    }, []);
    const canEdit = !readOnly;

    const { nodes: positioned, edgeLanes } = useMemo(() => layoutFlow(graph.nodes, graph.edges), [graph]);

    const issuesById = useMemo(
        () => (base ? computeGraphIssues(graph, { base, channels, labels }) : {}),
        [graph, base, channels, labels],
    );

    const renderedNodes = positioned.map(node => ({
        ...node,
        selected: node.id === panel.nodeId,
        data: { ...node.data, onDelete: canEdit ? onDeleteNode : undefined, issues: issuesById[node.id] },
    }));
    const renderedEdges = graph.edges.map(edge => ({
        ...edge,
        data: { ...edge.data, laneX: edgeLanes[edge.id], onInsert: canEdit ? onInsert : undefined },
    }));

    const selectedNode = graph.nodes.find(node => node.id === panel.nodeId) || null;

    const addStep = kind => {
        const result = insertNodeOnEdge(graph.nodes, graph.edges, panel.edgeId, kind);
        setGraph({ nodes: result.nodes, edges: result.edges });
        setPanel({ mode: 'settings', edgeId: null, nodeId: result.selectedId });
    };

    const updateNodeData = (id, data) =>
        setGraph(g => ({ ...g, nodes: g.nodes.map(node => (node.id === id ? { ...node, data } : node)) }));

    const save = async () => {
        if (!base?.title?.trim()) {
            dispatch(alertActions.error(t('AutomationsPage.editor.nameRequired')));
            return;
        }
        if (Object.keys(issuesById).length) {
            dispatch(alertActions.error(t('AutomationsPage.editor.fixIssues')));
            return;
        }
        setSaving(true);
        try {
            const model = flowToModel(base, graph.nodes, graph.edges);
            const saved = await automationsService.save(model);
            dispatch(alertActions.success(t('AutomationsPage.editor.saved')));
            onSaved?.(saved);
        } catch (error) {
            handleApiError(error, 'alertMessages.saveFail');
        }
        setSaving(false);
    };

    if (!base) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                background: '#f8fafc',
                zIndex: 1300,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <EditorHeader
                name={base.title}
                onNameChange={value => setBase({ ...base, title: value })}
                onBack={onClose}
                onSave={save}
                saving={saving}
                readOnly={readOnly}
            />
            <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
                <Box sx={{ flex: 1, position: 'relative' }}>
                    <ReactFlow
                        nodes={renderedNodes}
                        edges={renderedEdges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        onNodeClick={(_, node) => setPanel({ mode: 'settings', edgeId: null, nodeId: node.id })}
                        onPaneClick={() => setPanel({ mode: null, edgeId: null, nodeId: null })}
                        fitView
                        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
                    >
                        <Background />
                        <Controls showInteractive={false} />
                    </ReactFlow>
                </Box>

                {panel.mode === 'palette' && (
                    <RightPanel
                        title={t('AutomationsPage.editor.addStep')}
                        onClose={() => setPanel({ mode: null, edgeId: null, nodeId: null })}
                    >
                        <ActionPalette onSelect={addStep} />
                    </RightPanel>
                )}

                {panel.mode === 'settings' && selectedNode && (
                    <RightPanel
                        title={t(`AutomationsPage.nodes.${getNodeKind(selectedNode.data)}`)}
                        onClose={() => setPanel({ mode: null, edgeId: null, nodeId: null })}
                    >
                        <Box sx={readOnly ? { pointerEvents: 'none', opacity: 0.7 } : undefined}>
                            <NodeSettings
                                node={selectedNode}
                                base={base}
                                setBase={setBase}
                                updateNodeData={updateNodeData}
                                labels={labels}
                                readOnly={readOnly}
                                triggerActivityTypes={
                                    graph.nodes.find(n => n.data.type === AUTOMATION_NODE_TYPE.TRIGGER)?.data
                                        .activityTypes || []
                                }
                                triggerCustomEventType={
                                    graph.nodes.find(n => n.data.type === AUTOMATION_NODE_TYPE.TRIGGER)?.data
                                        .customEventType
                                }
                            />
                        </Box>
                    </RightPanel>
                )}
            </Box>
        </Box>
    );
};

export default Editor;
